import EditIcon from "@mui/icons-material/Edit";
import { IconButton } from "@mui/material";
import React from "react";
import { updateSubscription } from "../../../../../services/subscriptions.service";
import { parseDateHifen } from "../../../../../utils/parseDate";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ModalComponent } from "../../../../../ui-components/Modal";
import { useToast } from "../../../../../ui-components/ToastProvider";
import {
  adminButtonOutlinedSx,
  adminButtonPrimarySx,
} from "../../../modalStyle";

// Removendo estilo fixo para adotar modal padronizado do Admin

interface PreSubscription {
  id: number;
  user_id: number;
  event_id: number;
  first_time: boolean;
  start_date: Date;
  end_date: Date;
  status_id: number;
  created_at: Date;
  space_type: string;
  extra_table?: number;
  table_quantity?: number;
  extra_chair?: number;
  chair_quantity?: number;
  items?: string;
  notes?: string;
  amount?: number;
}

export default function UpdateButton({
  id,
  updateSubscriptions,
  sub,
}: {
  id: number;
  updateSubscriptions: Function;
  sub: PreSubscription;
}) {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [status, setStatus] = React.useState<number>(sub.status_id);
  const [submitting, setSubmitting] = React.useState(false);
  const { showToast } = useToast();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const formData: any = Object.fromEntries(new FormData(e.target).entries());
    setSubmitting(true);
    try {
      await updateSubscription(
        {
          status_id: status,
          space_type: formData.space_type,
          start_date: formData.start_date,
          end_date: formData.end_date,
          extra_table: Number(formData.extra_table),
          table_quantity: Number(formData.table_quantity) || 0,
          extra_chair: Number(formData.extra_chair),
          chair_quantity: Number(formData.chair_quantity) || 0,
          items: formData.items,
          notes: formData.notes,
          amount: Number(formData.amount) || 0,
        },
        id,
      );
      showToast("Pré-inscrição atualizada com sucesso!", { severity: "success" });
      handleClose();
      updateSubscriptions();
    } catch (err) {
      showToast("Erro ao atualizar pré-inscrição!", { severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ModalComponent
        open={open}
        handleClose={handleClose}
        title="Editar Pré-inscrição"
        maxWidth={isMobile ? "90%" : 700}
        autoHeight
      >
        <form onSubmit={handleSubmit}>
          <Box>
            <Typography variant="body2" sx={{ color: "#24324D", mb: 1 }}>
              Status da Pré-inscrição
            </Typography>
            <ToggleButtonGroup
              color="primary"
              exclusive
              value={status}
              onChange={(_, val) => {
                if (val !== null) setStatus(val);
              }}
              fullWidth
              orientation={isMobile ? "vertical" : "horizontal"}
              size={isMobile ? "small" : "medium"}
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: isMobile ? "column" : "row",
                "& .MuiToggleButton-root": {
                  flex: 1,
                  width: "100%",
                  textTransform: "none",
                  justifyContent: "flex-start",
                  padding: isMobile ? "10px 12px" : undefined,
                  borderRadius: isMobile ? 2 : 2,
                  gap: 1,
                },
                "& .Mui-selected": {
                  backgroundColor: "#eef2ff",
                  borderColor: "primary.main",
                  border: "0.5 0.5 0.5 0.5",
                },
              }}
            >
              <ToggleButton value={0} aria-label="Em Análise">
                <HourglassEmptyIcon fontSize="small" /> Em Análise
              </ToggleButton>
              <ToggleButton value={1} aria-label="Aceito">
                <CheckCircleIcon fontSize="small" color="success" /> Aceito
              </ToggleButton>
              <ToggleButton value={2} aria-label="Recusado">
                <CancelIcon fontSize="small" color="error" /> Recusado
              </ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="body2" sx={{ color: "#24324D", mt: 3, mb: 1 }}>
              Dados da Inscrição
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="space-type-label">Tipo de Espaço</InputLabel>
                  <Select
                    labelId="space-type-label"
                    name="space_type"
                    label="Tipo de Espaço"
                    defaultValue={sub.space_type || ""}
                  >
                    <MenuItem value="shared">Compartilhado</MenuItem>
                    <MenuItem value="exclusive">Exclusivo</MenuItem>
                    <MenuItem value="1_tendas">1 Tenda</MenuItem>
                    <MenuItem value="2_tendas">2 Tendas</MenuItem>
                    <MenuItem value="autoral">Marcas Autorais</MenuItem>
                    <MenuItem value="alimentacao">Praça de Alimentação</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Data de Início"
                  name="start_date"
                  defaultValue={parseDateHifen(sub.start_date)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Data de Fim"
                  name="end_date"
                  defaultValue={parseDateHifen(sub.end_date)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="extra-table-label">Mesa Extra</InputLabel>
                  <Select
                    labelId="extra-table-label"
                    name="extra_table"
                    label="Mesa Extra"
                    defaultValue={sub.extra_table ? 1 : 0}
                  >
                    <MenuItem value={1}>Sim</MenuItem>
                    <MenuItem value={0}>Não</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Qtd. Mesas"
                  name="table_quantity"
                  defaultValue={sub.table_quantity || 0}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="extra-chair-label">Cadeira Extra</InputLabel>
                  <Select
                    labelId="extra-chair-label"
                    name="extra_chair"
                    label="Cadeira Extra"
                    defaultValue={sub.extra_chair ? 1 : 0}
                  >
                    <MenuItem value={1}>Sim</MenuItem>
                    <MenuItem value={0}>Não</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Qtd. Cadeiras"
                  name="chair_quantity"
                  defaultValue={sub.chair_quantity || 0}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Itens"
                  name="items"
                  defaultValue={sub.items || ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Valor (R$)"
                  name="amount"
                  defaultValue={sub.amount || 0}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Observação"
                  name="notes"
                  defaultValue={sub.notes || ""}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: 3,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                pt: 1,
                flexDirection: isMobile ? "column" : "row-reverse",
                justifyContent: isMobile ? "flex-end" : "flex-start",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                type="submit"
                sx={adminButtonPrimarySx}
                fullWidth={isMobile}
                disabled={submitting}
              >
                Alterar
              </Button>
              <Button
                onClick={handleClose}
                sx={adminButtonOutlinedSx}
                fullWidth={isMobile}
                disabled={submitting}
              >
                Cancelar
              </Button>
            </Box>
          </Box>
        </form>
      </ModalComponent>
      <IconButton onClick={handleOpen}>
        <EditIcon />
      </IconButton>
    </>
  );
}
